/**
 * StateUtils.js
 * Utility for US state codes and mapping functions
 */

// US States with abbreviations
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' }
];

/**
 * Get state code from full name or partial text match
 * @param {string} stateText - State name or text to match
 * @returns {string|null} Two-letter state code or null if no match found
 */
export const getStateCodeFromText = (stateText) => {
  if (!stateText) return null;
  
  // If it's already a valid 2-letter code, return it
  if (/^[A-Z]{2}$/.test(stateText.toUpperCase())) {
    const isValidCode = US_STATES.some(state => state.code === stateText.toUpperCase());
    if (isValidCode) return stateText.toUpperCase();
  }
  
  // Clean up input text
  const normalizedText = stateText.trim().toLowerCase();
  
  // 1. Try exact match
  const exactMatch = US_STATES.find(
    state => state.name.toLowerCase() === normalizedText
  );
  if (exactMatch) return exactMatch.code;
  
  // 2. Try starts with match (for abbreviations like "New" for "New York")
  const startsWithMatch = US_STATES.find(
    state => state.name.toLowerCase().startsWith(normalizedText)
  );
  if (startsWithMatch) return startsWithMatch.code;
  
  // 3. Try contains match for partial text
  const containsMatch = US_STATES.find(
    state => state.name.toLowerCase().includes(normalizedText)
  );
  if (containsMatch) return containsMatch.code;
  
  // No match found
  return null;
};

/**
 * Get full state name from state code
 * @param {string} stateCode - Two-letter state code
 * @returns {string|null} Full state name or null if not found
 */
export const getStateNameFromCode = (stateCode) => {
  if (!stateCode) return null;
  
  const match = US_STATES.find(
    state => state.code === stateCode.toUpperCase()
  );
  
  return match ? match.name : null;
};

export default {
  US_STATES,
  getStateCodeFromText,
  getStateNameFromCode
}; 